import { listSingleResourceType } from "api/graphqlTypes";
import { FeConfigType } from "config/config";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { actions as dataStoreAction } from "utils/DataStore/DataStore.reducer";
import { FetchRecords } from "./interfaces";

// NB: the total `execution_count` is deliberately NOT selected here. Over a large
// execution table (400k+ rows) the exact COUNT joins+counts every matching row
// across the four polymorphic tables (record/identity/activity/execution) and
// takes ~2s server-side, whereas the 10-row page itself returns in ~tens of ms.
// Selecting the count inline forced the whole request — and therefore the first
// paint of the rows — to wait for the count. The count is fetched separately via
// `executionCountQueryString` so the rows paint immediately and the total fills
// the pagination footer a moment later.
// (operator-task-fe-advanced-search-execution-list-query-slow-3s)

// Standalone total-count query for the Executions tab, fetched in parallel with
// (and independently of) the row page above. The count is independent of
// pagination (limit/offset) and sort, so it declares only `$filter`. See the note
// on `executionListQueryString` for why the exact count is expensive (~2s) and
// therefore kept off the row-paint critical path.
// (operator-task-fe-advanced-search-execution-list-query-slow-3s)
export const executionCountQueryString = `query($filter: String) {
    execution_list(filter:$filter){
        execution_count
    },
}`;

export const executionListQueryString = `query($filter: String, $order: String, $limit: Int, $offset: Int) {
    execution_list(filter:$filter,order:$order,limit:$limit,offset:$offset){
        execution_list{
            id
            activity_type
            status
            message
            name
            modified_at
            modified_by_identity{
                identity_type
                id
                name
                is_deleted
            }
            created_at
            created_by_identity{
                identity_type
                id
                name
                is_deleted
            }
            deleted_at
            scheduled_for
            priority
            type
            is_productive
            use_breakpoints
            use_only_explicit_breakpoints
            pending_trash_identity_id
            pending_restore_identity_id
            pending_delete_identity_id
            comment_count
            started_at
            dependency_id
            dependency_id_dependency {
                return_when
                dependency_execution_list_dependency_id(limit:0, order: "+id") {
                    dependency_execution_count
                }
            }
            setting_id
            setting_id_setting {
                name
            }
            message_id
            message_id_message {
                subject
                body
                is_submitted
            }
            flow_id
            flow_id_flow {
                name
            }
            schedule_id
            schedule_id_schedule {
                name
            }
            wrapper_id
            wrapper_id_wrapper {
                name
            }
            connector_type
            connector_id
            connector_id_connector{
                name
            }
            sync_config_id
            sync_config_id_identity{
                name
            }
            is_remote_processed
            last_command
            lineno
        }
    },
}`;

export const messageListQuery = `query($filter: String, $order: String, $limit: Int, $offset: Int) {
    message_list(filter:$filter,order:$order,limit:$limit,offset:$offset){
        message_count
        message_list{
            id
            activity_type
            name
            modified_at
            modified_by_identity{
                identity_type
                id
                name
                is_deleted
            }
            response
            deleted_at
        }
    },
}`;
export const activityQueryString = "";

// NB: has_deprecation / has_syntax_error are intentionally NOT part of this shared
// record/resource union selection. As of backend commit 9209968ee those two
// columns live only on the child tables that populate them (flow/wrapper/scheduler:
// both; connector: has_deprecation); the polymorphic base record/resource OBJECT
// type no longer exposes them, so selecting them here fails graphql validation
// ("Cannot query field 'has_deprecation' on type 'resource'") and blanks Advanced
// Search (Type: all/file/custom_object/execution) + the dashboard LatestEdits
// widget. Per-type screens fetch them via their own config bodies
// (config/resources/{flow,wrapper,scheduler,connector}.tsx); the Type:all union
// list therefore does not render deprecation/syntax badges — the accepted
// consequence of the base-table narrowing.
export const recordListQueryString = `
    id
    name
    description
    record_type
    resolved_icon
    resolved_object_template_id
    is_readonly
    modified_at
    modified_by_identity{
        id
        identity_type
        name
        is_deleted
    }
    created_at
    created_by_identity{
        identity_type
        id
        name
        is_deleted
    }
    deleted_at
    project_id
    bundle_id
    project_id_project{
        name
        id
        icon
    }
    bundle_id_bundle {
        name
        id
        icon 
    }
    is_bundle_readonly
    is_project_readonly
    track_in_git
    pending_trash_identity_id
    pending_restore_identity_id
    pending_delete_identity_id
    comment_count
`;

export const resourceListQueryString = `
    ${recordListQueryString}
    resource_type
    is_bundle_content`;

export const resourceQueryString = (
    type = "resource"
) => `query($filter: String, $order: String, $limit: Int, $offset: Int) {
    ${type}_list(filter:$filter,order:$order,limit:$limit,offset:$offset){
        ${type}_count
        ${type}_list {
            ${resourceListQueryString}
            workspace_id_workspace{
                id
                name
            }
        }
    }
}`;

export const advancedSearchQueryString = (
    resourceType = "",
    {
        includeCustomObjectValue = false,
        additionalListingFields = [],
    }: { includeCustomObjectValue?: boolean; additionalListingFields?: string[] } = {}
) => {
    // Once a custom-object search is scoped to a single object template, also
    // fetch the `value` blob so the template's attribute columns (value.<attr>)
    // have data to render. Mirrors the dashboard record-list widget, which
    // likewise requests `value` only when an object template is selected — the
    // blob is not pulled for unscoped custom-object searches.
    // (operator-task-fe-advanced-search-co-attribute-columns)
    //
    // `additionalListingFields` are the top-level scalar fields whose generic
    // schema-driven column is currently VISIBLE — fetched only when displayed
    // (Option B). (operator-task-fe-advanced-search-file-content-type-column)
    //
    // `resolved_icon` feeds the Name column's RecordLabel image. The per-type
    // listing bodies (config/resources/*.tsx) do not select it, so without it a
    // single-type search (e.g. Type = Dashboard) shows only the generic type icon
    // instead of each record's own icon. Every resource list type exposes it.
    // (operator-task-fe-advanced-search-dashboard-own-icon-not-shown)
    const extraFields = [
        "resolved_icon",
        includeCustomObjectValue && resourceType === "custom_object" ? "value" : "",
        ...additionalListingFields,
    ]
        .filter(Boolean)
        .join("\n");
    return {
        executions: executionListQueryString,
        messages: messageListQuery,
        resources:
            resourceType === "resource"
                ? resourceQueryString(resourceType)
                : listSingleResourceType(resourceType as FeConfigType, extraFields),
    };
};

export const useFetchAdvancedRecords = () => {
    const dispatch = useDispatch();

    const fetchRecords = useCallback(
        ({ key, restFilter, filter, includeDeleted, hideDeleted, queryString }: FetchRecords) => {
            return new Promise((resolve, reject) => {
                dispatch({
                    promise: { resolve, reject },
                    type: dataStoreAction.START_FETCH_DATA,
                    key,
                    params: {
                        query: queryString,
                        variables: {
                            ...restFilter,
                            filter,
                        },
                        allow_normal: hideDeleted,
                        allow_deleted: includeDeleted,
                    },
                });
            });
        },
        [dispatch]
    );

    // Fetch the Executions-tab total count on its own dataStore key, in parallel
    // with the row page. The previous count is cleared first so the footer shows
    // the loading provisional (not a stale total) until the fresh count resolves.
    // (operator-task-fe-advanced-search-execution-list-query-slow-3s)
    const fetchExecutionCount = useCallback(
        ({
            key,
            filter,
            includeDeleted,
            hideDeleted,
        }: {
            key: string;
            filter: string | null;
            includeDeleted?: boolean;
            hideDeleted?: boolean;
        }) => {
            dispatch({ type: dataStoreAction.CLEAN_DATA, key });
            dispatch({
                type: dataStoreAction.START_FETCH_DATA,
                key,
                params: {
                    query: executionCountQueryString,
                    variables: { filter },
                    allow_normal: hideDeleted,
                    allow_deleted: includeDeleted,
                },
            });
        },
        [dispatch]
    );

    const fetchUserList = () => {
        dispatch({
            type: dataStoreAction.START_FETCH_DATA,
            key: "allUsersPlainList",
            params: {
                query: `{
                    user_plain_list(limit:1000,order:"+name"){
                        id
                        name
                        icon
                    }
                }`,
            },
        });
    };

    return {
        fetchRecords,
        fetchExecutionCount,
        fetchUserList,
    };
};
